import React, { useState } from 'react';

import { unparseConditionToUi } from '../rules/signals/utils';
import {
  SignalCardData,
  SignalCardGroup,
  SignalCardRule,
  ruleCount,
} from './signalCardData';
import './signalCard.css';
import { OpenPage } from './RulesCard';
import { ModelPageTarget } from './rulesCardData';

/**
 * The signals a write just added, drawn from the write's own receipt.
 *
 * What a rep will read, and which way each one argues, is the whole first glance: these
 * sentences land on a lead record and nothing else about them matters to the person
 * approving them. So the closed card is direction, group and sentence, and the rules
 * underneath — the condition that fires each one, and its priority — open on request.
 *
 * Nothing here is composed. The sentence is the one that was written, and the condition
 * goes through `unparseConditionToUi`, the parser the Signals tab reads a rule back with,
 * so a rule cannot read one way here and another way there.
 */
export function SignalCard({
  card,
  onOpen,
}: {
  card: SignalCardData;
  onOpen?: (target: ModelPageTarget) => void;
}) {
  const [open, setOpen] = useState(false);
  const rules = ruleCount(card);
  const groups = card.groups.length;

  return (
    <section className={`msig${open ? ' msig--open' : ''}`}>
      <button
        type="button"
        className="msig-hd"
        aria-expanded={open}
        onClick={() => setOpen((was) => !was)}
      >
        <span className="msig-what">
          Added {rules} {rules === 1 ? 'signal' : 'signals'}
          {groups > 1 ? ` across ${groups} groups` : ''}
        </span>
        <span className="msig-more">
          <span>{open ? 'Hide what fires them' : 'What fires them'}</span>
          <span className="msig-chev" aria-hidden="true" />
        </span>
      </button>

      {card.groups.map((group, i) => (
        <Group key={`${group.group}-${i}`} group={group} open={open} />
      ))}
      <div className="msig-foot">
        <OpenPage label="Open Signals" target="signals" onOpen={onOpen} />
      </div>
    </section>
  );
}

function Group({ group, open }: { group: SignalCardGroup; open: boolean }) {
  return (
    <div className="msig-group">
      <p className="msig-name">
        {group.group}
        {open && group.column && <code>{group.column}</code>}
      </p>
      <ul className="msig-rules">
        {(group.rules ?? []).map((rule, i) => (
          <Rule key={`${rule.says}-${i}`} rule={rule} open={open} />
        ))}
      </ul>
      {open && (group.dropped?.length ?? 0) > 0 && (
        <ul className="msig-rules msig-gone">
          {group.dropped?.map((gone, i) => (
            <li key={`${gone}-${i}`} className="msig-rule">
              <span className="msig-sign" aria-hidden="true">
                ×
              </span>
              <span className="msig-body">
                <span className="msig-says">{gone}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
      {open && group.order_decides_the_outcome && (
        <p className="msig-order">
          A lead can match more than one of these. The first that matches is the
          one it is told, and the rest are never checked.
        </p>
      )}
    </div>
  );
}

function Rule({ rule, open }: { rule: SignalCardRule; open: boolean }) {
  // `direction` here is the LOGICAL joiner the parser takes ('AND' | 'OR'), not the
  // signal's plus or minus. A written rule is one condition, so it is always 'AND'.
  const parsed = rule.condition
    ? unparseConditionToUi(rule.condition, 'AND')
    : null;
  const against = rule.argues_for === false;

  return (
    <li className={`msig-rule${against ? ' msig-rule--against' : ''}`}>
      <span className="msig-sign" aria-label={against ? 'counts against' : 'counts for'}>
        {against ? '−' : '+'}
      </span>
      <span className="msig-body">
        <span className="msig-says">{rule.says}</span>
        {open && parsed && (
          <span className="msig-when">
            <b>{parsed.field}</b> {parsed.verb}{' '}
            {(parsed.values ?? []).filter(Boolean).join(', ')}
            {typeof rule.priority === 'number' && (
              <em> · priority {rule.priority}</em>
            )}
          </span>
        )}
      </span>
    </li>
  );
}
